import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Infinity, Sparkles, X } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { DjayAvatar, useDjaySelection } from '../mascot';
interface FreePracticeIntroProps {
  isOpen: boolean;
  onStart: () => void;
  onCancel: () => void;
}
export function FreePracticeIntro({
  isOpen,
  onStart,
  onCancel
}: FreePracticeIntroProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  return <AnimatePresence data-id="element-2372">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-2373">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onCancel} data-id="element-2374" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-2375">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-emerald-500 p-6 text-white text-center relative" data-id="element-2376">
              <button onClick={onCancel} className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors" data-id="element-2377">
                <X className="w-5 h-5" data-id="element-2378" />
              </button>
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md" data-id="element-2379">
                <Compass className="w-8 h-8 text-white" data-id="element-2380" />
              </div>
              <h2 className="text-2xl font-black tracking-tight" data-id="element-2381">
                Vrij Oefenen
              </h2>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8" data-id="element-2382">
              <div className="flex gap-4 items-start mb-6" data-id="element-2383">
                <div className="flex-shrink-0 mt-1" data-id="element-2384">
                  <DjayAvatar size="md" emotion="excited" variant={selectedVariant} animate={true} data-id="element-2385" />
                </div>
                <div className="bg-teal-50 rounded-2xl rounded-tl-none p-4 border border-teal-100 text-teal-900 text-sm font-medium leading-relaxed" data-id="element-2386">
                  Bij Vrij Oefenen kies je zelf wat je wilt doen. Geen regels,
                  geen tijdslimiet — jij bepaalt!
                </div>
              </div>

              <div className="space-y-4 mb-8" data-id="element-2387">
                <div className="flex items-start gap-3" data-id="element-2388">
                  <div className="mt-0.5 bg-teal-100 p-1 rounded-full text-teal-600" data-id="element-2389">
                    <Compass className="w-4 h-4" data-id="element-2390" />
                  </div>
                  <div data-id="element-2391">
                    <p className="text-sm font-bold text-slate-800" data-id="element-2392">
                      Jouw keuze
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2393">
                      Kies elk onderwerp dat je wilt oefenen.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2394">
                  <div className="mt-0.5 bg-emerald-100 p-1 rounded-full text-emerald-600" data-id="element-2395">
                    <Infinity className="w-4 h-4" data-id="element-2396" />
                  </div>
                  <div data-id="element-2397">
                    <p className="text-sm font-bold text-slate-800" data-id="element-2398">
                      Geen limiet
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2399">
                      Oefen zo lang of kort als je wilt.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2400">
                  <div className="mt-0.5 bg-amber-100 p-1 rounded-full text-amber-600" data-id="element-2401">
                    <Sparkles className="w-4 h-4" data-id="element-2402" />
                  </div>
                  <div data-id="element-2403">
                    <p className="text-sm font-bold text-slate-800" data-id="element-2404">
                      Alle vakken
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2405">
                      Alles is beschikbaar — ook onderwerpen uit andere groepen.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-2406">
                <Button variant="secondary" onClick={onCancel} className="flex-1" data-id="element-2407">
                  Niet nu
                </Button>
                <Button variant="primary" onClick={onStart} className="flex-1 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 border-none shadow-lg shadow-teal-200" data-id="element-2408">
                  Start!
                </Button>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}